import React, { FC, Fragment, useEffect, useRef } from 'react';
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    isSameDay,
    isWithinInterval,
    isEqual,
    startOfDay,
    isSameMonth,
    isSameYear,
    subDays,
} from 'date-fns';
import { CalendarResponseDto, DocumentType } from '@/features/calendar/types/calendar-dto';
import { cn } from '@/lib/utils';
import { CalendarConfig } from '@/features/calendar/utils/CalendarConfig';
import { useTranslation } from 'react-i18next';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { DateTimeHelper } from '@/utils/dateTimeHelper';

type Props = {
    selectedDate: Date;
    showReservationDetails: (
        id: string,
        type: DocumentType,
        apartmentId?: string,
        checkInDate?: Date,
        checkOutDate?: Date,
    ) => void;
} & CalendarResponseDto;

const CalendarDailyBody: FC<Props> = ({ apartments, selectedDate, showReservationDetails }) => {
    const today = new Date();
    const { t } = useTranslation();

    const isCurrentMonth = isSameMonth(selectedDate, today) && isSameYear(selectedDate, today);

    const daysInMonth = eachDayOfInterval({
        start: startOfMonth(selectedDate),
        end: endOfMonth(selectedDate),
    });

    const calendarRef = useRef<HTMLDivElement>(null);
    const todayRef = useRef<HTMLTableCellElement>(null);

    useEffect(() => {
        if (todayRef.current && calendarRef.current) {
            calendarRef.current.scrollLeft =
                todayRef.current.offsetLeft -
                calendarRef.current.clientWidth / 2 +
                todayRef.current.clientWidth / 2;
        }
    }, []);

    const handleMouseDown = (event: React.MouseEvent) => {
        if (!calendarRef.current) return;
        const startX = event.pageX;
        const scrollLeft = calendarRef.current.scrollLeft;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (!calendarRef.current) return;
            const deltaX = moveEvent.pageX - startX;
            calendarRef.current.scrollLeft = scrollLeft - deltaX;
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <div
            ref={calendarRef}
            className="w-0 grow overflow-x-auto no-scrollbar active:cursor-grabbing"
            onMouseDown={handleMouseDown}
        >
            <table className="relative border-none">
                <thead>
                    <tr>
                        {daysInMonth.map((day) => (
                            <th
                                key={day.toISOString()}
                                ref={isSameDay(day, today) ? todayRef : null}
                                className="p-0 border border-popover-100 bg-popover-10 border-t-0 last:border-r-0 select-none"
                            >
                                <div className="w-30 p-6 py-4 flex items-start flex-col">
                                    <span className="text-xs font-medium text-popover-600">
                                        {format(day, 'EEE').slice(0, 2)}
                                    </span>
                                    <span className="text-sm font-medium">{format(day, 'dd')}</span>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {apartments?.map((apartment, apartmentIndex) => (
                        <tr key={`body-${apartment.apartmentId}`}>
                            {daysInMonth.map((day) => {
                                const hideApartmentData =
                                    apartment.documents?.some((doc) =>
                                        DateTimeHelper.shouldRenderBookingInMonth(
                                            selectedDate,
                                            doc.checkInDate,
                                            doc.checkOutDate,
                                        ),
                                    );

                                if (!hideApartmentData) return null;

                                const bookedDays =
                                    apartment.documents?.filter((doc) =>
                                        isWithinInterval(day, {
                                            start: startOfDay(doc.checkInDate),
                                            end: startOfDay(subDays(doc.checkOutDate, 1)),
                                        }),
                                    ) || [];

                                const firstBookedDay = apartment.documents?.find((doc) =>
                                    isEqual(startOfDay(day), startOfDay(doc.checkInDate)),
                                );

                                const monthStart = startOfMonth(selectedDate);
                                const continuationDay = !firstBookedDay && isEqual(startOfDay(day), startOfDay(monthStart))
                                    ? apartment.documents?.find((doc) =>
                                        startOfDay(doc.checkInDate) < startOfDay(monthStart) &&
                                        isWithinInterval(day, {
                                            start: startOfDay(doc.checkInDate),
                                            end: startOfDay(subDays(doc.checkOutDate, 1)),
                                        })
                                    )
                                    : undefined;

                                const labelDay = firstBookedDay || continuationDay;

                                const isFirstTodayCell =
                                    isSameDay(day, today) && apartmentIndex === 0;

                                return (
                                    <td
                                        key={`cell-${apartment.apartmentId}-${day}`}
                                        className="relative h-23 p-0 border border-popover-100 border-r-0 border-b-0 bg-popover-10 hover:bg-popover-50 transition-all ease-out duration-300 select-none"
                                    >
                                        {labelDay && (
                                            <div className="px-6 flex flex-col items-start gap-0.5 inset-0">
                                                <span className="text-xs font-medium text-popover-600">
                                                    {t(
                                                        CalendarConfig.getEventNameKey(
                                                            labelDay.eventType,
                                                        ),
                                                    )}
                                                </span>
                                                <span className="text-sm font-medium">
                                                    <CurrencyValue
                                                        value={labelDay.monthRevenue}
                                                        asset="calendar"
                                                    />
                                                </span>
                                                {labelDay.revenue !== labelDay.monthRevenue && (
                                                    <span className="text-xs text-popover-600">
                                                        <CurrencyValue
                                                            value={labelDay.revenue}
                                                            asset="calendar"
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {bookedDays.map((doc) => (
                                            <Fragment key={`event-bar-${doc.documentId}-${day}`}>
                                                <div
                                                    className={cn(
                                                        `absolute w-full h-1 bottom-0`,
                                                        CalendarConfig.getEventColor(doc.eventType)
                                                            ?.eventColor,
                                                    )}
                                                />
                                                <div
                                                    className="absolute inset-0 cursor-pointer"
                                                    onClick={() =>
                                                        showReservationDetails(
                                                            doc.documentId,
                                                            doc.documentType,
                                                            apartment.apartmentId,
                                                            doc.checkInDate,
                                                            doc.checkOutDate,
                                                        )
                                                    }
                                                />
                                            </Fragment>
                                        ))}
                                        {isSameDay(day, today) && isCurrentMonth && (
                                            <div className="absolute top-0 left-1/2 w-0.5 h-full bg-red-500 transform -translate-x-1/2">
                                                {isFirstTodayCell && (
                                                    <div className="absolute -top-1 left-1/2 size-2 bg-red-500 rounded-full transform -translate-x-1/2" />
                                                )}
                                            </div>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default CalendarDailyBody;
